import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { IonicModule } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';

import { PageLayoutModule } from '../global/page-layout';
import { ImageService } from '../global/images/image.service';
import { AppEvent } from './models';
import { EventService } from './event.service';

/**
 * The games this page currently browses across. Deliberately just the two with an events feature
 * today (bolt-action-3e, k47) — matching the plan's stated reasoning that their audiences overlap
 * enough to want one shared discovery surface. Adding a third game here is a one-line change.
 */
const BROWSABLE_GAMES: { id: string; labelKey: string }[] = [
    { id: 'bolt-action-3e', labelKey: 'GLOBAL.MENU_ITEMS.BOLT_ACTION_3E' },
    { id: 'k47', labelKey: 'GLOBAL.MENU_ITEMS.K47' }
];

type EventSortField = 'date' | 'spaces' | 'location';
type EventSortDirection = 'asc' | 'desc';

/**
 * A genuinely top-level, game-agnostic events browse page — additive alongside the existing
 * per-game event routes (`/bolt-action-3e/events`, `/k47/events`), which stay exactly as they are.
 * Deliberately minimal: no title/date/country/near-me filters (those stay on the per-game list
 * page for now) — just a game filter and a list, so a visitor doesn't need to already know which
 * game's menu to look under. Tapping through to an event still lands on that event's own per-game
 * detail route, since EventDetailPage depends on that game module's DI (ForceService et al).
 *
 * Standalone, and not wired into any game module's routing — see the top-level `/events` route in
 * app-routing.module.ts, which resolves route-data `gameId: 'home'` so it automatically gets the
 * same neutral homepage background as `/home`/`/games` via ThemeService, with no extra styling.
 */
@Component({
    standalone: true,
    selector: 'abs-event-browse',
    imports: [CommonModule, RouterModule, IonicModule, TranslateModule, PageLayoutModule],
    template: `
        <abs-page-layout [pageTitle]="'GLOBAL.EVENTS.BROWSE_TITLE' | translate">
            <!--
                A dropdown rather than segment tabs on purpose: tabs consume a fixed, limited
                horizontal row that a third/fourth browsable game would immediately overflow — a
                select scales to any number of games with no layout change.
            -->
            <form>
                <div class="horizontal">
                    <div class="form-row">
                        <ion-item>
                            <ion-select
                                [label]="'GLOBAL.EVENTS.FILTER_GAME' | translate"
                                labelPlacement="stacked"
                                [value]="gameFilter()"
                                (ionChange)="setFilter($any($event.detail.value))"
                            >
                                <ion-select-option value="all">{{ 'GLOBAL.EVENTS.TAB_ALL' | translate }}</ion-select-option>
                                @for (game of games; track game.id) {
                                    <ion-select-option [value]="game.id">{{ game.labelKey | translate }}</ion-select-option>
                                }
                            </ion-select>
                        </ion-item>
                    </div>
                    <div class="form-row">
                        <ion-item>
                            <ion-select
                                [label]="'GLOBAL.EVENTS.SORT_BY' | translate"
                                labelPlacement="stacked"
                                [value]="sortValue()"
                                (ionChange)="setSort($any($event.detail.value))"
                            >
                                <ion-select-option value="date|asc">{{ 'GLOBAL.EVENTS.SORT_DATE_ASC' | translate }}</ion-select-option>
                                <ion-select-option value="date|desc">{{ 'GLOBAL.EVENTS.SORT_DATE_DESC' | translate }}</ion-select-option>
                                <ion-select-option value="spaces|asc">{{ 'GLOBAL.EVENTS.SORT_SPACES_ASC' | translate }}</ion-select-option>
                                <ion-select-option value="spaces|desc">{{ 'GLOBAL.EVENTS.SORT_SPACES_DESC' | translate }}</ion-select-option>
                                <ion-select-option value="location|asc">{{ 'GLOBAL.EVENTS.SORT_LOCATION_ASC' | translate }}</ion-select-option>
                                <ion-select-option value="location|desc">{{ 'GLOBAL.EVENTS.SORT_LOCATION_DESC' | translate }}</ion-select-option>
                            </ion-select>
                        </ion-item>
                    </div>
                </div>
                <ion-item lines="none">
                    <ion-toggle [checked]="includePastEvents()" (ionChange)="toggleIncludePast($event.detail.checked)">
                        {{ 'GLOBAL.EVENTS.FILTER_INCLUDE_PAST' | translate }}
                    </ion-toggle>
                </ion-item>
            </form>

            @if (loading()) {
                <ion-spinner name="crescent"></ion-spinner>
            } @else if (sortedEvents().length === 0) {
                <div class="empty-state">
                    <ion-icon name="calendar-outline"></ion-icon>
                    <p>{{ 'GLOBAL.EVENTS.NO_EVENTS' | translate }}</p>
                </div>
            } @else {
                @for (event of sortedEvents(); track event._id) {
                    <div class="tile tile--interactive event-card" [routerLink]="['/', event.gameId, 'events', event._id]">
                        <!-- Always rendered, so cards in a grid row are the same shape whether or
                             not the event has a banner — matches event-list.page.ts's own card. -->
                        <div class="event-card-media" [class.event-card-media--empty]="!event.imageId">
                            @if (event.imageId) {
                                <img [src]="imageService.url(event.imageId)" alt="" class="event-card-img" />
                            } @else {
                                <ion-icon name="calendar-outline"></ion-icon>
                            }
                        </div>
                        <div class="event-card-body">
                            <div class="tile-title">{{ event.title }}</div>
                            <div class="tile-subtitle">
                                @if (event.location?.city) {
                                    {{ event.location.city }},&nbsp;
                                }
                                {{ event.location?.countryCode }} &mdash; {{ event.startDate | date: 'd MMM y' }}
                            </div>
                            <div class="tile-subtitle">{{ gameLabel(event.gameId) | translate }}</div>
                            @let spaces = spacesRemaining(event);
                            <div class="tile-subtitle">
                                @if (spaces > 0) {
                                    {{ 'GLOBAL.EVENTS.SPACES_REMAINING' | translate: { count: spaces } }}
                                } @else {
                                    {{ 'GLOBAL.EVENTS.EVENT_FULL' | translate }}
                                }
                            </div>
                        </div>
                    </div>
                }
            }
        </abs-page-layout>
    `
    // Styles reused from the shared global stylesheets — .tile/.tile-title/.tile-subtitle live in
    // tile-grid.scss, .event-card/.event-card-media/.event-card-img/.empty-state in events.scss —
    // both already used by event-list.page.ts's own card; nothing new needed here.
})
export class EventBrowsePage implements OnInit {
    private eventService = inject(EventService);
    imageService = inject(ImageService);

    games = BROWSABLE_GAMES;
    gameFilter = signal<string>('all');
    events = signal<AppEvent[]>([]);
    loading = signal(true);
    // Past events are excluded server-side by default (EventService.findAll) — this only
    // controls whether we ask for them too, not any client-side filtering of its own.
    includePastEvents = signal(false);

    // Date ascending matches the API's own default ordering (findAll sorts by startDate),
    // so the initial render needs no client-side re-sort to look right.
    sortField = signal<EventSortField>('date');
    sortDirection = signal<EventSortDirection>('asc');
    sortValue = computed(() => `${this.sortField()}|${this.sortDirection()}`);

    filteredEvents = computed(() => {
        const filter = this.gameFilter();
        return filter === 'all' ? this.events() : this.events().filter((e) => e.gameId === filter);
    });

    sortedEvents = computed(() => {
        const field = this.sortField();
        const direction = this.sortDirection() === 'asc' ? 1 : -1;
        return [...this.filteredEvents()].sort((a, b) => this.compareEvents(a, b, field) * direction);
    });

    ngOnInit() {
        this.fetchEvents();
    }

    setFilter(gameId: string) {
        this.gameFilter.set(gameId);
    }

    toggleIncludePast(checked: boolean) {
        this.includePastEvents.set(checked);
        this.fetchEvents();
    }

    private fetchEvents() {
        this.loading.set(true);
        this.eventService.listEventsAcrossGames(this.games.map((g) => g.id), this.includePastEvents()).subscribe({
            next: (events) => {
                this.events.set(events);
                this.loading.set(false);
            },
            error: () => this.loading.set(false)
        });
    }

    setSort(value: string) {
        const [field, direction] = value.split('|') as [EventSortField, EventSortDirection];
        this.sortField.set(field);
        this.sortDirection.set(direction);
    }

    gameLabel(gameId: string): string {
        return this.games.find((g) => g.id === gameId)?.labelKey ?? gameId;
    }

    // Never negative: registeredCount can only reach capacity under normal operation
    // (register() blocks further active registrations once full), but clamping here
    // keeps the display sane even if it ever briefly overshoots.
    spacesRemaining(event: AppEvent): number {
        return Math.max(0, event.capacity - (event.registeredCount ?? 0));
    }

    private locationKey(event: AppEvent): string {
        return `${event.location?.countryCode ?? ''} ${event.location?.city ?? ''}`.trim().toLowerCase();
    }

    private compareEvents(a: AppEvent, b: AppEvent, field: EventSortField): number {
        switch (field) {
            case 'spaces':
                return this.spacesRemaining(a) - this.spacesRemaining(b);
            case 'location':
                return this.locationKey(a).localeCompare(this.locationKey(b));
            case 'date':
            default:
                return new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
        }
    }
}
